import {
  Table,
  TableCell,
  TableHead,
  styled,
  Typography,
  Theme,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo } from 'react';
import { InvoiceState, columns } from './Invoices.const';
import { StyledTableBody } from '../teamMemberView/StyledTableBody';
import { StyledTableRow } from '../teamMemberView/StyledTableRow';
import { PageWrapper } from '../../components';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { getSelectedAccount } from '../../redux/account';
import { useTitle } from '../../hooks/useTitle';
import { PageTitle } from '../../utils/pageTitles';
import { useParams } from 'react-router-dom';
import {
  Invoice,
  fetchProjectInvoicesData,
  invoicesSlice,
  selectinvoicesData,
} from '../../redux/invoices';
import { DataState } from '../../types/asyncData';
import { InvoiceDataRow } from './components/InvoiceDataRow';
import { InvoicesFilter } from './components/InvoicesFilter';

const StyledInvoicesTable = styled(Table)`
  background: ${({ theme }) => theme.palette.background.paper};
  border-radius: 0 0 ${({ theme }) => theme.spacing(1)}
    ${({ theme }) => theme.spacing(1)};
  border: 1;
`;

export const Invoices = () => {
  const dispatch = useAppDispatch();
  const columnWidth = (theme: Theme) => theme.spacing(15);
  const account = useAppSelector(getSelectedAccount);
  const { accountId } = useParams();

  const invoicesPageData = useAppSelector(selectinvoicesData);
  const {
    areFiltersApplied,
    filterBy: { invoiceState, projectNames },
  } = invoicesPageData;

  const filterInvoicesByState = useCallback(
    (invoices: Invoice[], invoiceState: InvoiceState) => {
      const currentDate = new Date();

      switch (invoiceState) {
        case InvoiceState.ALL:
          return invoices;
        case InvoiceState.OVERDUE:
          return invoices.filter(
            (invoice) =>
              !invoice.paidOn && new Date(invoice.invoiceDueDate) < currentDate,
          );
        case InvoiceState.OPEN:
          return invoices.filter(
            (invoice) =>
              new Date(invoice.invoiceDueDate) > currentDate && !invoice.paidOn,
          );
        case InvoiceState.PAIDAFTERDUE:
          return invoices.filter((invoice) => invoice.isPaidAfterPastDue);
        case InvoiceState.PAID:
          return invoices.filter(
            (invoice) => invoice.paidOn && !invoice.isPaidAfterPastDue,
          );
        default:
          return invoices;
      }
    },
    [],
  );

  const invoicesData = useMemo(() => {
    let invoices = invoicesPageData.invoices.data ?? [];

    if (!invoices.length) return [];

    // Filter by project names if provided
    if (projectNames.length) {
      invoices = invoices.filter(
        (invoice: Invoice) =>
          invoice.projectName && projectNames.includes(invoice.projectName),
      );
    }

    // Filter invoices based on the selected invoice state
    return filterInvoicesByState(invoices, invoiceState);
  }, [invoiceState, projectNames, invoicesPageData, filterInvoicesByState]);

  const invoicesDataState = invoicesPageData.invoices.state;
  const setPageTitle = useTitle();

  useEffect(() => {
    setPageTitle(
      `${account?.name ?? PageTitle.portfolio} | ${PageTitle.invoices}`,
    );
  }, [setPageTitle, account]);

  useEffect(() => {
    if (accountId) {
      dispatch(
        fetchProjectInvoicesData({
          accountId: accountId,
        }),
      );
    }
    return () => {
      dispatch(invoicesSlice.actions.resetFilters());
    };
  }, [dispatch, accountId]);

  return (
    <PageWrapper isLoading={invoicesDataState === DataState.Pending}>
      <InvoicesFilter />
      <StyledInvoicesTable
        stickyHeader
        sx={{ border: '1px solid rgba(0, 0, 0, 0.1);' }}
      >
        <TableHead>
          <StyledTableRow isLastRow={false}>
            {columns.map((columnName: string) => (
              <TableCell
                sx={{ width: columnWidth }}
                key={columnName}
                variant="head"
                align={columnName === 'Amount' ? 'right' : 'left'}
              >
                <Typography
                  textAlign={columnName === 'Amount' ? 'right' : 'left'}
                  sx={{ cursor: 'default' }}
                  variant="h4"
                  component="p"
                >
                  {columnName}
                </Typography>
              </TableCell>
            ))}
          </StyledTableRow>
        </TableHead>
        <StyledTableBody>
          {invoicesData && invoicesData.length ? (
            invoicesData.map((invoice: Invoice) => (
              <InvoiceDataRow data={invoice} key={invoice.projectName} />
            ))
          ) : (
            <StyledTableRow isLastRow={true} isHidden={true}>
              <TableCell align="left">
                <Typography
                  whiteSpace="nowrap"
                  textAlign="left"
                  variant="h4"
                  color="slategray"
                >
                  {`${
                    areFiltersApplied
                      ? 'No invoices are available in this account for the selected filters.'
                      : 'No overdue invoices are available in this account.'
                  }`}
                </Typography>
              </TableCell>
            </StyledTableRow>
          )}
        </StyledTableBody>
      </StyledInvoicesTable>
    </PageWrapper>
  );
};
